import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'

const PREFIX = '/yeu-cau-sua-chua'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

// ===== Báo cáo tổng hợp =====
/**
 * Bộ chỉ số đếm theo DÒNG thiết bị (bỏ phiếu đã hủy). Phễu loại trừ nhau:
 * tongThietBi = chuaXong + choTraKhach + daTra; chuaXong = choKhachChot + dangSua + khongSuaChoDong.
 * daXong = choTraKhach + daTra; chuaTra = chuaXong + choTraKhach.
 * Kết quả: hoanThanh = suaXong + khachKhongSua + khongCoLinhKien + khongThayLoi + chuaRoLyDo.
 */
export type BaoCaoMetric = {
  tongPhieu: number
  tongThietBi: number
  chuaXong: number
  choKhachChot: number
  dangSua: number
  khongSuaChoDong: number
  daXong: number
  choTraKhach: number
  daTra: number
  chuaTra: number
  /** Tập con của chuaXong: đã quá ngày hẹn trả khách. */
  quaHan: number
  hoanThanh: number
  suaXong: number
  khachKhongSua: number
  khongCoLinhKien: number
  khongThayLoi: number
  chuaRoLyDo: number
  tongPhi: number
  soNgayXuLyTB: number | null
}

/** Kỳ: ngày `YYYY-MM-DD`, tuần ISO `YYYY-WW`, tháng `YYYY-MM`. */
export type BaoCaoTongHopKyRow = BaoCaoMetric & { ky: string }

/** kyThuatVienId = 0 → chưa phân công. */
export type BaoCaoTongHopKyThuatVienRow = BaoCaoMetric & {
  kyThuatVienId: number
  tenKyThuatVien: string
}

export type BaoCaoTongHopLoaiYeuCauRow = BaoCaoMetric & { loaiYeuCau: string | null }

/** Tháng `YYYY-MM` theo ngày tạo phiếu (giờ VN). */
export type BaoCaoTongHopDuAnThangRow = BaoCaoMetric & { duAnId: number; thang: string }

export type BaoCaoTongHopDuAnThangKtvRow = BaoCaoTongHopDuAnThangRow & {
  kyThuatVienId: number
  tenKyThuatVien: string
}

export type BaoCaoTongHopYeuCauSuaChua = {
  /** Dự án đang đăng nhập — chỉ số của dự án này mới mở được danh sách. */
  duAnHienTai: number
  /** Dự án đang được thống kê. */
  duAnIds: number[]
  duAnChoPhep: Array<{ id: number; ma: string; ten: string }>
  chiNhanhChoPhep: Array<{ id: number; ten: string; duAnId: number }>
  tongCong: BaoCaoMetric
  ketQuaTheoKy: BaoCaoTongHopKyRow[]
  theoKyThuatVien: BaoCaoTongHopKyThuatVienRow[]
  theoLoaiYeuCau: BaoCaoTongHopLoaiYeuCauRow[]
  theoDuAnThang: BaoCaoTongHopDuAnThangRow[]
  theoDuAnThangKtv: BaoCaoTongHopDuAnThangKtvRow[]
}

/**
 * Query báo cáo tổng hợp → params GET.
 * Parse riêng (không dùng `banHangListParamsFromQueryString`) để giữ nguyên key
 * `chiNhanhId` — util danh sách đang đổi nhiều chi nhánh thành `chiNhanhIds`
 * mà `BaoCaoTongHopQueryDto` bên BE chỉ nhận `chiNhanhId`.
 */
const baoCaoTongHopParams = (search?: string): Record<string, unknown> => {
  if (!search) return {}
  const parsed = queryString.parse(search.replace(/^\?/, '')) as Record<
    string,
    string | string[] | null | undefined
  >
  const out: Record<string, unknown> = {}
  for (const key of ['tuNgay', 'denNgay', 'duAnId', 'chiNhanhId', 'kyThuatVienId', 'nhomTheo'] as const) {
    const v = parsed[key]
    if (v == null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length === 1) out[key] = v[0]
      else if (v.length > 1) out[key] = v
    } else {
      out[key] = v
    }
  }
  return out
}

export const taoYeuCauSuaChua = (body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-yeu-cau-sua-chua`, body))

export const fetchDanhSachYeuCauSuaChua = (search: string): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-yeu-cau-sua-chua`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as Record<string, unknown>[], paging, summary }
    }),
  )
}

export const fetchDanhSachHangSuaChua = (search: string): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-hang-sua-chua`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      const mapped = (data as Record<string, unknown>[]).map((row) => {
        const yeuCau = row.yeuCau as Record<string, unknown> | undefined
        const yeuCauId = (yeuCau?.id as string) ?? row.yeuCauId ?? row.id
        return {
          ...row,
          // BUG-3: GIỮ id = ID DÒNG (unique key bảng — nhiều dòng cùng phiếu phải khác key,
          // ghi đè id = yeuCauId gây React duplicate key → render thừa dòng cũ).
          // Edit/expand trỏ về phiếu cha qua `yeuCauId` (editIdFromRow + InlineDetail).
          yeuCauId,
          maYeuCau: (yeuCau?.maYeuCau as string) ?? row.maYeuCau ?? '',
          tenKhachHang: (yeuCau?.tenKhachHang as string) ?? row.tenKhachHang ?? '',
          // Cột "Trạng thái" của danh sách = trạng thái phiếu cha (dòng thiết bị không có trangThai)
          trangThai: yeuCau?.trangThai ?? row.trangThai,
          chiTietId: row.id,
        }
      })
      return { data: mapped, paging, summary }
    }),
  )
}

export const xuatFileYeuCauSuaChuaBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-yeu-cau-sua-chua`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const xuatFileHangSuaChuaBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-hang-sua-chua`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const fetchYeuCauSuaChuaDetail = (id: string | number) =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-yeu-cau-sua-chua`).then((res: unknown) => ({
      data: normalizeDetailResponse(res as { data?: Record<string, unknown> }),
    })),
  )

export const capNhatYeuCauSuaChua = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-yeu-cau-sua-chua`, body))

export const huyYeuCauSuaChua = (id: string | number) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/huy-yeu-cau-sua-chua`, {}))

export const ketThucYeuCauSuaChua = (id: string | number) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/ket-thuc-yeu-cau-sua-chua`, {}))

export const xuLyYeuCauSuaChua = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/xu-ly-yeu-cau-sua-chua`, body))

export const traHangYeuCauSuaChua = (id: string | number, body: Record<string, unknown> = {}) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/tra-hang-yeu-cau-sua-chua`, body))

/**
 * Body trả hàng YCSC (G3-B) — 2 nhánh semantic:
 * - Tường minh `chiTietIds` = OVERWRITE các dòng đó (kể cả dòng đã trả) + BE recap rollup phiếu.
 * - Bỏ trống `chiTietIds` = "trả tất cả" an toàn (chỉ chạm dòng chưa trả, không reset lịch sử).
 */
export type TraHangYeuCauSuaChuaBody = {
  chiTietIds?: Array<string | number>
  ngayTra?: string
  nguoiNhanTraId?: number
}

export const fetchBaoCaoTongHopYeuCauSuaChua = (search?: string): Promise<BaoCaoTongHopYeuCauSuaChua> =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/bao-cao-tong-hop-yeu-cau-sua-chua`, {
      params: baoCaoTongHopParams(search),
    }).then(
      (res: unknown) =>
        normalizeDetailResponse(res as { data?: BaoCaoTongHopYeuCauSuaChua }) as BaoCaoTongHopYeuCauSuaChua,
    ),
  )

export const xuatFileBaoCaoTongHopBlob = async (search?: string): Promise<Blob> => {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-bao-cao-tong-hop`, {
      ...blobResponseConfig,
      params: baoCaoTongHopParams(search),
    }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}
